import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';

export const UnattributedRevenueChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const { chartData, totalUnattributed, totalRevenue } = useMemo(() => {
        if (!finance?.dailyBreakdown) {
            return { chartData: [], totalUnattributed: 0, totalRevenue: 0 };
        }

        let sumUnattributed = 0;
        let sumTotal = 0;

        const data = Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const breakdown = (day.breakdown as Array<Record<string, unknown>>) || [];
                let attributed = 0;
                let unattributed = 0;

                for (const item of breakdown) {
                    if (item.type === 'REVENUE' && item.category === 'product_sale') {
                        const amount = Number(item.value) || 0;
                        const traffic = item.traffic as Record<string, unknown> | null;
                        const hasAttribution =
                            traffic &&
                            (traffic.target || traffic.source || traffic.utm_source);

                        if (hasAttribution) {
                            attributed += amount;
                        } else {
                            unattributed += amount;
                        }
                    }
                }

                sumUnattributed += unattributed;
                sumTotal += attributed + unattributed;

                return { date, attributed, unattributed };
            });

        return { chartData: data, totalUnattributed: sumUnattributed, totalRevenue: sumTotal };
    }, [finance?.dailyBreakdown]);

    const unattributedPercent = useMemo(
        () => (totalRevenue > 0 ? Math.round((totalUnattributed / totalRevenue) * 10000) / 100 : 0),
        [totalUnattributed, totalRevenue]
    );

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="px-4 pt-3 pb-1 flex items-baseline gap-3 [container-type:inline-size]">
                <span
                    className="font-semibold"
                    style={{
                        color: CHART_COLORS.accent,
                        fontSize: 'clamp(1rem, 6cqi, 2rem)',
                    }}
                >
                    {formatCompactNumber(totalUnattributed, currencySymbol)}
                </span>
                <span className="text-[10px] text-primary opacity-60">
                    {unattributedPercent}% {t('charts.unattributed', 'unattributed')}
                </span>
            </div>
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(value, currencySymbol),
                                name === 'unattributed'
                                    ? t('charts.unattributed', 'Unattributed')
                                    : t('charts.attributed', 'Attributed'),
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <Legend
                            verticalAlign="bottom"
                            iconType="circle"
                            wrapperStyle={{ fontSize: '10px' }}
                            formatter={(value: string) =>
                                value === 'unattributed'
                                    ? t('charts.unattributed', 'Unattributed')
                                    : t('charts.attributed', 'Attributed')
                            }
                        />
                        <Bar
                            dataKey="attributed"
                            stackId="rev"
                            fill={CHART_COLORS.chart.line2}
                            radius={[0, 0, 0, 0]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                        <Bar
                            dataKey="unattributed"
                            stackId="rev"
                            fill={CHART_COLORS.accent}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
